import {useQuery} from "@apollo/client";
import pick from "lodash/pick";
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {FormProvider} from "react-hook-form";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {ANALYZER_DEBUG} from "@/lib/featureFlags.ts";
import {AnalyzerSource} from "@/lib/queries/analyzerRuns.ts";
import {AccountStatsResults, GQL_ACCOUNT_STATS} from "@/lib/queries/billing.ts";
import {AccountUsageResponse, CheckFormResult, GQL_ACCOUNT_USAGE, GQL_CHECK_FORM} from "@/lib/queries/checks.ts";
import {AccountSubscriptionType} from "@/lib/types/accounts.ts";
import {AccountStats} from "@/lib/types/billing.ts";
import {AccountServicesUsage} from "@/lib/types/checks.ts";
import {
  ANALYZER_DEFAULT_LOCATIONS_CAP,
  ANALYZER_RESPOND_URL,
  ANALYZER_STREAM_URL,
} from "@/pages/analyzer/Analyzer.constants.ts";
import {
  AnalyzerPhase,
  AnalyzerProposalPatch,
  BulkCreateChecksOutcome,
  CheckLimitTierLookup,
  MonitoringReport,
  ProposedCheck,
  ProposedGroup,
  SSECheckSelectionEvent,
  SSEEvent,
  SSEEventType,
  SSEInterviewEvent,
  SSEReportEvent,
  SSERespondAction,
} from "@/pages/analyzer/Analyzer.types.ts";
import {
  assignPositions,
  buildCheckLimitTierLookup,
  canApplyIntervalToProposal,
  carrySelection,
  coupleGroupSelection,
  getHiddenTypes,
  getLatestCheckSelectionEvent,
  getLocationsForProposal,
  getUncreatedMemberIds,
  normalizeProposalOverrides,
  orderChecks,
  orderReportRows,
  preselectChecks,
  resolveContactGroupIds,
  resolveContactGroupNames,
  toGroupProposal,
} from "@/pages/analyzer/Analyzer.utils.ts";
import {useAnalyzerCreateForm} from "@/pages/analyzer/hooks/useAnalyzerCreateForm.ts";
import {useSSEStream} from "@/pages/analyzer/hooks/useSSEStream.ts";

type SSEStreamReturn = ReturnType<typeof useSSEStream>;

interface AnalyzerContextValue extends SSEStreamReturn {
  showDebug: boolean;
  isActive: boolean;
  handleSubmit: (url: string, source: AnalyzerSource) => Promise<void>;
  handleRetry: () => void;
  handleAnswer: (answer: string, event: SSEInterviewEvent) => void;
  handleRevision: (request: string) => void;
  pendingRevisionRequest?: string;
  answers: Record<string, string>;
  analyzedDomain: string;
  formData?: CheckFormResult;
  refetchFormData: () => void;
  trackPreCreatedTag: () => void;
  getPreCreatedTagsCount: () => number;
  resetPreCreatedTagsCount: () => void;
  report?: MonitoringReport;
  proposedChecks: ProposedCheck[];
  orderedChecks: ProposedCheck[];
  recommendedChecks: ProposedCheck[];
  positionById: Map<string, number>;
  proposedGroups: ProposedGroup[];
  awaitingProposals: boolean;
  selectedProposals: ProposedCheck[];
  updateProposedChecks: (patch: AnalyzerProposalPatch) => void;
  selectedChecks: Record<string, boolean>;
  setSelectedChecks: Dispatch<SetStateAction<Record<string, boolean>>>;
  accountUsage?: AccountServicesUsage;
  getTier: CheckLimitTierLookup;
  limitsLoading: boolean;
  refetchUsage: () => Promise<AccountServicesUsage | undefined>;
  refetchStats: () => Promise<AccountStats | undefined>;
  creationOutcome: BulkCreateChecksOutcome | null;
  applyCreationOutcome: (outcome: BulkCreateChecksOutcome) => void;
  isCreating: boolean;
  setIsCreating: Dispatch<SetStateAction<boolean>>;
  pollEdit: SSEInterviewEvent | null;
  startPollEdit: (event: SSEInterviewEvent) => void;
  cancelPollEdit: () => void;
  activeToolGroup: string | null;
  setActiveToolGroup: Dispatch<SetStateAction<string | null>>;
  resultsEvents: SSEEvent[];
}

const AnalyzerContext = createContext<AnalyzerContextValue | null>(null);

export const useAnalyzerContext = (): AnalyzerContextValue => {
  const context = useContext(AnalyzerContext);
  if (!context) {
    throw new Error("useAnalyzerContext must be used within AnalyzerProvider");
  }
  return context;
};

interface AnalyzerProviderProps {
  children: ReactNode;
}

export const AnalyzerProvider = ({children}: AnalyzerProviderProps) => {
  const {user, userHasFeature} = useCurrentUserContext();
  const stream = useSSEStream();
  const {reset, startStream, retry, respond, phase, events} = stream;
  const showDebug = !!userHasFeature(ANALYZER_DEBUG);
  const isActive = phase !== AnalyzerPhase.IDLE;

  const isBilling2 = user?.account.subscription_type === AccountSubscriptionType.BILLING_2_0;
  const {
    data: usageData,
    loading: usageLoading,
    refetch: refetchUsageQuery,
  } = useQuery<AccountUsageResponse>(GQL_ACCOUNT_USAGE);
  const {
    data: statsData,
    loading: statsLoading,
    refetch: refetchStatsQuery,
  } = useQuery<AccountStatsResults>(GQL_ACCOUNT_STATS, {
    skip: !isBilling2,
  });
  const limitsLoading = usageLoading || statsLoading;
  const accountUsage = usageData?.accountServicesUsage;
  const getTier = useMemo(
    () => buildCheckLimitTierLookup(isBilling2, accountUsage, statsData?.accountStats.items[0]),
    [isBilling2, accountUsage, statsData]
  );

  const refetchUsage = useCallback(
    async () => (await refetchUsageQuery().catch(() => undefined))?.data?.accountServicesUsage,
    [refetchUsageQuery]
  );
  const refetchStats = useCallback(
    async () => (await refetchStatsQuery().catch(() => undefined))?.data?.accountStats.items[0],
    [refetchStatsQuery]
  );

  const resultsEvents = useMemo(() => {
    const lastAnswerIndex = events.reduce(
      (last, event, index) => (event.type === SSEEventType.ANSWER ? index : last),
      -1
    );
    return lastAnswerIndex >= 0 ? events.slice(lastAnswerIndex + 1) : events;
  }, [events]);
  const [revisionPending, setRevisionPending] = useState(false);
  const selectionEvent = useMemo(() => {
    const latest = getLatestCheckSelectionEvent(resultsEvents);
    if (latest || !revisionPending) {
      return latest;
    }
    return getLatestCheckSelectionEvent(events);
  }, [resultsEvents, events, revisionPending]);
  const hasWriteAccess = !!user?.has_write_access;
  const {data: formData, refetch: refetchFormData} = useQuery<CheckFormResult>(GQL_CHECK_FORM, {
    skip: !hasWriteAccess,
    fetchPolicy: "network-only",
  });
  const locationsCap = user?.account.limit_monitoring_groups || ANALYZER_DEFAULT_LOCATIONS_CAP;
  const preCreatedTagsCountRef = useRef(0);
  const trackPreCreatedTag = useCallback(() => {
    preCreatedTagsCountRef.current += 1;
  }, []);
  const getPreCreatedTagsCount = useCallback(() => preCreatedTagsCountRef.current, []);
  const resetPreCreatedTagsCount = useCallback(() => {
    preCreatedTagsCountRef.current = 0;
  }, []);
  const report = useMemo(
    () => [...events].reverse().find((event): event is SSEReportEvent => event.type === SSEEventType.REPORT)?.content,
    [events]
  );
  const [selectedChecks, setRawSelectedChecks] = useState<Record<string, boolean>>({});
  const [patchesById, setPatchesById] = useState<Record<string, AnalyzerProposalPatch>>({});
  const [creationOutcome, setCreationOutcome] = useState<BulkCreateChecksOutcome | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const positionsRef = useRef(new Map<string, number>());
  const updateProposedChecks = useCallback(
    (patch: AnalyzerProposalPatch) =>
      setPatchesById((current) => {
        const next = {...current};
        for (const [id, selected] of Object.entries(selectedChecks)) {
          if (selected) {
            next[id] = {...next[id], ...patch};
          }
        }
        return next;
      }),
    [selectedChecks]
  );
  const {
    checks: proposedChecks,
    recommendedChecks,
    positionById,
    proposedGroups,
    uniformTags,
    uniformContactGroups,
  } = useMemo(() => {
    const checks = (selectionEvent?.checks ?? []).map((check) => {
      const patch = patchesById[check.id];
      return {
        ...check,
        ...(patch?.msp_interval &&
          (!formData || canApplyIntervalToProposal(formData, check, patch.msp_interval)) && {
            msp_interval: patch.msp_interval,
          }),
        ...(patch?.num_locations &&
          (!formData || getLocationsForProposal(formData, check).length > 0) && {
            num_locations: patch.num_locations,
          }),
      };
    });
    const capped = checks.map((check) => {
      if (!formData && !check.num_locations) {
        return check;
      }
      const assignable = formData ? getLocationsForProposal(formData, check).length : locationsCap;
      const numLocations = check.num_locations ? Math.min(check.num_locations, assignable) : assignable;
      if (!numLocations) {
        return check;
      }
      return {
        ...check,
        num_locations: numLocations,
        ...(Number(check.msp_sensitivity) > numLocations && {msp_sensitivity: String(numLocations)}),
      };
    });
    const eventGroups = selectionEvent?.groups ?? [];
    const groups = report ? eventGroups : eventGroups.filter(({creatable}) => creatable);
    const ordered = orderReportRows(
      capped,
      report?.report_only_checks ?? [],
      accountUsage,
      positionsRef.current,
      groups
    );
    if (accountUsage) {
      positionsRef.current = assignPositions(positionsRef.current, ordered.orderedIds);
    }
    const groupProposals = groups
      .filter(({creatable}) => creatable)
      .map((group) => toGroupProposal(group, ordered.proposals, positionsRef.current));
    const normalized = normalizeProposalOverrides(capped);
    return {
      ...normalized,
      checks: [...normalized.checks, ...groupProposals],
      recommendedChecks: [...checks, ...groupProposals],
      positionById: positionsRef.current,
      proposedGroups: groups,
    };
  }, [selectionEvent, formData, locationsCap, patchesById, report, accountUsage]);
  const orderedChecks = useMemo(
    () => orderChecks(proposedChecks, accountUsage, ({id}) => positionById.get(id)),
    [proposedChecks, accountUsage, positionById]
  );
  const createdByProposalId = useMemo(() => creationOutcome?.createdByProposalId ?? {}, [creationOutcome]);
  const setSelectedChecks: Dispatch<SetStateAction<Record<string, boolean>>> = useCallback(
    (update) =>
      setRawSelectedChecks((current) => {
        const next = typeof update === "function" ? update(current) : update;
        return coupleGroupSelection(current, next, proposedChecks, createdByProposalId);
      }),
    [proposedChecks, createdByProposalId]
  );
  const applyCreationOutcome = useCallback(
    (outcome: BulkCreateChecksOutcome) => {
      setCreationOutcome(outcome);
      setRawSelectedChecks((current) => {
        const stillSelectable = proposedChecks.filter(
          (check) =>
            !outcome.createdByProposalId[check.id] ||
            getUncreatedMemberIds(check, outcome.createdByProposalId).length > 0
        );
        const next = pick(
          current,
          stillSelectable.map(({id}) => id)
        );
        return coupleGroupSelection(current, next, proposedChecks, outcome.createdByProposalId);
      });
    },
    [proposedChecks]
  );
  const selectedProposals = useMemo(
    () => proposedChecks.filter(({id}) => selectedChecks[id]),
    [proposedChecks, selectedChecks]
  );
  const awaitingProposals =
    (phase === AnalyzerPhase.STREAMING || phase === AnalyzerPhase.INTERVIEW) && !proposedChecks.length;
  const [analyzedDomain, setAnalyzedDomain] = useState("");
  const [pendingRevision, setPendingRevision] = useState<string>();
  const revisionEchoed = events.some((event) => event.type === SSEEventType.ANSWER && event.answer === pendingRevision);
  const pendingRevisionRequest =
    pendingRevision && phase === AnalyzerPhase.STREAMING && !revisionEchoed ? pendingRevision : undefined;
  const [pollEdit, setPollEdit] = useState<SSEInterviewEvent | null>(null);
  const [activeToolGroup, setActiveToolGroup] = useState<string | null>(null);
  const answers = useMemo(() => {
    const byCheckpoint: Record<string, string> = {};
    for (const event of events) {
      if (event.type === SSEEventType.ANSWER) {
        byCheckpoint[event.checkpoint_id] = event.answer;
      }
    }
    return byCheckpoint;
  }, [events]);
  const lastSelectionRef = useRef<{event: SSECheckSelectionEvent; checks: ProposedCheck[]} | null>(null);
  const submitPendingRef = useRef(false);

  const startPollEdit = useCallback((event: SSEInterviewEvent) => setPollEdit(event), []);
  const cancelPollEdit = useCallback(() => setPollEdit(null), []);

  useEffect(() => {
    if (limitsLoading || !selectionEvent) {
      return;
    }
    const previous = lastSelectionRef.current;
    if (previous?.event === selectionEvent && previous.checks === proposedChecks) {
      return;
    }
    const isNewSelection = previous?.event !== selectionEvent;
    lastSelectionRef.current = {event: selectionEvent, checks: proposedChecks};
    setSelectedChecks((current) =>
      previous ? carrySelection(previous.checks, current, proposedChecks) : preselectChecks(proposedChecks)
    );
    if (isNewSelection) {
      setCreationOutcome(
        (outcome) =>
          outcome && {
            ...outcome,
            errorsByProposalId: pick(
              outcome.errorsByProposalId,
              proposedChecks.map((check) => check.id)
            ),
          }
      );
    }
  }, [selectionEvent, limitsLoading, proposedChecks, setSelectedChecks]);

  const {methods, resetCreateForm} = useAnalyzerCreateForm({
    tags: formData?.tags,
    contactGroups: formData?.contactGroups.items,
    analyzedDomain,
    timezone: user?.account.timezone,
  });

  useEffect(() => {
    if (uniformTags) {
      const formTags = methods.getValues("tags");
      const resolved = uniformTags.flatMap((name) => {
        const formTag = formTags.find((tag) => tag.tag.toLowerCase() === name.toLowerCase());
        if (formTag) {
          return [formTag];
        }
        const catalogTag = formData?.tags.find((tag) => tag.tag.toLowerCase() === name.toLowerCase());
        return catalogTag ? [{id: catalogTag.id, tag: catalogTag.tag, color_hex: catalogTag.color_hex}] : [];
      });
      if (resolved.length) {
        methods.setValue("tags", resolved, {shouldDirty: true});
      }
    }
    if (uniformContactGroups) {
      methods.setValue(
        "contactGroups",
        resolveContactGroupIds(uniformContactGroups, formData?.contactGroups.items ?? []),
        {shouldDirty: true}
      );
    }
  }, [uniformTags, uniformContactGroups, methods, formData]);

  const handleSubmit = useCallback(
    async (url: string, source: AnalyzerSource) => {
      if (submitPendingRef.current) {
        return;
      }
      reset();
      resetCreateForm();
      setSelectedChecks({});
      setPatchesById({});
      setRevisionPending(false);
      setPendingRevision(undefined);
      setAnalyzedDomain(new URL(url).hostname);
      setCreationOutcome(null);
      setIsCreating(false);
      setPollEdit(null);
      setActiveToolGroup(null);
      lastSelectionRef.current = null;
      preCreatedTagsCountRef.current = 0;
      positionsRef.current = new Map();
      let checkForm = formData;
      if (!checkForm && hasWriteAccess) {
        submitPendingRef.current = true;
        checkForm = (await refetchFormData().catch(() => undefined))?.data;
        submitPendingRef.current = false;
      }
      const hiddenTypes = checkForm ? getHiddenTypes(checkForm) : [];
      startStream(ANALYZER_STREAM_URL, {url, hidden_types: hiddenTypes}, source);
    },
    [reset, resetCreateForm, startStream, setSelectedChecks, formData, hasWriteAccess, refetchFormData]
  );

  const handleRetry = useCallback(() => {
    setPendingRevision(undefined);
    retry(ANALYZER_RESPOND_URL);
  }, [retry]);

  const handleAnswer = useCallback(
    (answer: string, event: SSEInterviewEvent) => {
      const {checkpoint_id, sub_checkpoint_id, sub_checkpoint_ns} = event;
      const isReanswer = !!checkpoint_id && checkpoint_id in answers;
      setPollEdit(null);
      setRevisionPending(false);
      respond(ANALYZER_RESPOND_URL, answer, {
        checkpointId: checkpoint_id,
        ...(isReanswer && {subCheckpointId: sub_checkpoint_id, subCheckpointNs: sub_checkpoint_ns}),
      });
    },
    [respond, answers]
  );

  const handleRevision = useCallback(
    (request: string) => {
      const selectedIds = Object.keys(selectedChecks).filter((key) => selectedChecks[key]);
      const checkPositions = Object.fromEntries(
        orderedChecks.flatMap(({id}) => {
          const position = positionById.get(id);
          return position === undefined ? [] : [[id, position]];
        })
      );
      const values = methods.getValues();
      const contactCatalog = formData?.contactGroups.items ?? [];
      setRevisionPending(true);
      setPendingRevision(request);
      respond(ANALYZER_RESPOND_URL, request, {
        action: SSERespondAction.REVISE,
        selectedIds,
        availableTags: (formData?.tags ?? []).map(({tag}) => tag),
        availableContactGroups: contactCatalog.map(({name}) => name),
        currentTags: values.tags.map(({tag}) => tag),
        currentContactGroups: resolveContactGroupNames(values.contactGroups, contactCatalog),
        checkPositions,
        createdIds: Object.keys(createdByProposalId),
      });
    },
    [respond, selectedChecks, methods, formData, orderedChecks, positionById, createdByProposalId]
  );

  return (
    <AnalyzerContext.Provider
      value={{
        ...stream,
        showDebug,
        isActive,
        handleSubmit,
        handleRetry,
        handleAnswer,
        handleRevision,
        pendingRevisionRequest,
        answers,
        analyzedDomain,
        formData,
        refetchFormData,
        trackPreCreatedTag,
        getPreCreatedTagsCount,
        resetPreCreatedTagsCount,
        report,
        proposedChecks,
        orderedChecks,
        recommendedChecks,
        positionById,
        proposedGroups,
        awaitingProposals,
        selectedProposals,
        updateProposedChecks,
        selectedChecks,
        setSelectedChecks,
        accountUsage,
        getTier,
        limitsLoading,
        refetchUsage,
        refetchStats,
        creationOutcome,
        applyCreationOutcome,
        isCreating,
        setIsCreating,
        pollEdit,
        startPollEdit,
        cancelPollEdit,
        activeToolGroup,
        setActiveToolGroup,
        resultsEvents,
      }}
    >
      <FormProvider {...methods}>{children}</FormProvider>
    </AnalyzerContext.Provider>
  );
};
